"use client";

import React, {
  useMemo,
  useState,
  useRef,
  useCallback,
  useEffect,
} from "react";
import { Clock, ArrowLeft, ArrowRight, Calendar } from "lucide-react";
import { cn } from "../lib/utils";
import { Badge } from "./ui/badge";

// Helper functions matching page.tsx
const pad = (n) => n.toString().padStart(2, "0");
const toISODateOnly = (d) =>
  `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const timeToMinutes = (time) => {
  const [hours, minutes] = time.split(":").map(Number);
  return hours * 60 + minutes;
};

// Generate time slots based on clinic operating hours (matching page.tsx)
const generateTimeSlots = (date, operatingHours) => {
  // Default time slots if no operating hours defined
  const defaultSlots = [
    "07:00",
    "07:30",
    "08:00",
    "08:30",
    "09:00",
    "09:30",
    "10:00",
    "10:30",
    "11:00",
    "11:30",
    "12:00",
    "12:30",
    "13:00",
    "13:30",
    "14:00",
    "14:30",
    "15:00",
    "15:30",
    "16:00",
    "16:30",
    "17:00",
    "17:30",
    "18:00",
    "18:30",
    "19:00",
    "19:30",
    "20:00",
    "20:30",
    "21:00",
  ];

  if (!operatingHours?.weekly) {
    return defaultSlots;
  }

  const dateStr = toISODateOnly(date);

  // Check for exceptions first
  const exception = operatingHours.exceptions?.find(
    (ex) => ex.date === dateStr,
  );

  let ranges;
  let closed = false;

  if (exception) {
    closed = exception.closed;
    ranges = exception.ranges;
  } else {
    // Use weekly schedule
    const dayOfWeek = date.getDay(); // 0 = Sunday, 1 = Monday, etc.
    const dayNames = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"];
    const dayKey = dayNames[dayOfWeek];
    const dayHours = operatingHours.weekly[dayKey];

    if (dayHours) {
      closed = dayHours.closed;
      ranges = dayHours.ranges;
    }
  }

  // If closed or no ranges defined, return default
  if (closed || !ranges || ranges.length === 0) {
    return defaultSlots;
  }

  // Generate 30-minute slots for each range
  const slots = [];
  for (const range of ranges) {
    const startMinutes = timeToMinutes(range.open);
    const endMinutes = timeToMinutes(range.close);

    // Generate slots every 30 minutes
    for (let minutes = startMinutes; minutes < endMinutes; minutes += 30) {
      const hours = Math.floor(minutes / 60);
      const mins = minutes % 60;
      slots.push(`${pad(hours)}:${pad(mins)}`);
    }
  }

  return slots.length > 0 ? slots : defaultSlots;
};

// Get local date string (YYYY-MM-DD) without timezone conversion
const getLocalDateStr = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

// Get day label for date
const getDayLabel = (date) => {
  return new Intl.DateTimeFormat("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
  }).format(date);
};

// Get week header (e.g., "Monday, November 3, 2025")
const getWeekHeader = (startDate) => {
  const firstDay = startDate;
  const lastDay = new Date(startDate);
  lastDay.setDate(lastDay.getDate() + 6);

  return new Intl.DateTimeFormat("en-US", {
    weekday: "long",
    month: "long",
    day: "numeric",
    year: "numeric",
  }).format(firstDay);
};

// Convert minutes to time string
const minutesToTime = (minutes) => {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return `${hours.toString().padStart(2, "0")}:${mins
    .toString()
    .padStart(2, "0")}`;
};

// Calculate block position and height
const calculateBlockDimensions = (startTime, duration, availableSlots) => {
  const slots = availableSlots || [];
  const startMinutes = timeToMinutes(startTime);
  const startSlotIndex = slots.findIndex((slot) => {
    const slotMinutes = timeToMinutes(slot);
    return slotMinutes >= startMinutes;
  });

  const endMinutes = startMinutes + duration;
  const endSlotIndex = slots.findIndex((slot) => {
    const slotMinutes = timeToMinutes(slot);
    return slotMinutes >= endMinutes;
  });

  if (startSlotIndex === -1) return null;

  const slotHeight = 60; // 60px per 30-minute slot
  const top = startSlotIndex * slotHeight;
  const height =
    endSlotIndex === -1
      ? slotHeight
      : (endSlotIndex - startSlotIndex) * slotHeight;

  return { top, height, startSlotIndex };
};

export default function ScheduleView({
  startDate,
  therapistName,
  currentAppointment,
  onAppointmentMove,
  scheduleData = {}, // { [date]: { slots: [], breaks: [], unavailability: [] } }
  isLoading = false,
  onWeekChange,
  operatingHours = null, // Clinic operating hours
  therapistAvailability = null, // Therapist availability schedule
}) {
  const [draggedAppointment, setDraggedAppointment] = useState(null);
  const [dragTarget, setDragTarget] = useState(null);
  const containerRef = useRef(null);
  const scrollContainerRef = useRef(null);
  const autoScrollIntervalRef = useRef(null);
  const wheelScrollHandlerRef = useRef(null);

  // Generate time slots dynamically based on clinic operating hours and date
  const timeSlots = useMemo(() => {
    // For now, use the first day's time slots. In the future, could vary by day
    return generateTimeSlots(startDate, operatingHours);
  }, [startDate, operatingHours]);

  // Check if therapist is available at a given time slot (matching page.tsx logic)
  const isTherapistAvailable = useCallback(
    (timeSlot, date) => {
      if (!therapistAvailability) {
        return false; // No availability set means not available
      }

      // Get day of week
      const dayOfWeek = date.getDay(); // 0 = Sunday, 1 = Monday, etc.
      const dayNames = [
        "sunday",
        "monday",
        "tuesday",
        "wednesday",
        "thursday",
        "friday",
        "saturday",
      ];
      const dayKey = dayNames[dayOfWeek];
      const dayAvailability = therapistAvailability[dayKey];

      // Check if it's a DayAvailability object (not slotDuration which is a number)
      if (!dayAvailability || typeof dayAvailability === "number") {
        return false; // Not a valid day availability
      }

      if (!dayAvailability.isWorking) {
        return false; // Not working on this day
      }

      // Check if time slot is within working hours
      const isAvailable =
        timeSlot >= dayAvailability.startTime &&
        timeSlot < dayAvailability.endTime;

      return isAvailable;
    },
    [therapistAvailability],
  );

  // Generate extended days: 14 days before startDate + 7 days from startDate + 21 days after = 42 days total
  const days = useMemo(() => {
    const dates = [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Generate past dates (14 days before startDate, up to today)
    const start = new Date(startDate);
    start.setDate(start.getDate() - 14);

    // Ensure we don't go before today
    if (start < today) {
      start.setTime(today.getTime());
    }

    // Generate all dates from start (past) to 21 days after startDate (future)
    const endDate = new Date(startDate);
    endDate.setDate(endDate.getDate() + 21);

    const current = new Date(start);
    while (current <= endDate) {
      dates.push({
        date: new Date(current),
        dateStr: getLocalDateStr(current),
        label: getDayLabel(current),
        isToday: current.toDateString() === today.toDateString(),
      });
      current.setDate(current.getDate() + 1);
    }

    return dates;
  }, [startDate]);

  // Calculate current appointment block dimensions
  const appointmentBlock = useMemo(() => {
    if (!currentAppointment) return null;

    const aptDate = new Date(currentAppointment.startsAt);
    // Use local date string to avoid timezone shift issues
    const aptDateStr = getLocalDateStr(aptDate);
    const dayIndex = days.findIndex((d) => d.dateStr === aptDateStr);

    if (dayIndex === -1) return null;

    // Use local time, not UTC
    const startTime = `${aptDate
      .getHours()
      .toString()
      .padStart(2, "0")}:${aptDate.getMinutes().toString().padStart(2, "0")}`;
    const duration = currentAppointment.durationMins || 60;

    const dimensions = calculateBlockDimensions(startTime, duration, timeSlots);
    if (!dimensions) return null;

    return {
      ...dimensions,
      dayIndex,
      appointment: currentAppointment,
    };
  }, [currentAppointment, days]);

  // Handle drag start
  const handleDragStart = useCallback(
    (e) => {
      if (!appointmentBlock) {
        e.preventDefault();
        return;
      }
      e.stopPropagation();
      e.dataTransfer.effectAllowed = "move";
      e.dataTransfer.setData("text/plain", ""); // Required for Firefox
      e.dataTransfer.setData(
        "application/json",
        JSON.stringify(appointmentBlock),
      );
      setDraggedAppointment(appointmentBlock);

      // Create a custom drag image for better visibility
      const dragElement = e.currentTarget;
      const rect = dragElement.getBoundingClientRect();

      const dragPreview = document.createElement("div");
      dragPreview.style.cssText = `
        position: absolute;
        top: -9999px;
        left: -9999px;
        width: ${rect.width}px;
        height: ${rect.height}px;
        background-color: #fef3c7;
        border: 2px solid #f59e0b;
        border-radius: 6px;
        padding: 8px;
        font-family: system-ui, -apple-system, sans-serif;
        font-size: 12px;
        font-weight: bold;
        color: #92400e;
        display: flex;
        align-items: center;
        justify-content: center;
        box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
        z-index: 9999;
      `;

      dragPreview.innerHTML = `
        <span>${currentAppointment.durationMins || 60}min - ${
        currentAppointment.therapist || "Appointment"
      }</span>
      `;

      document.body.appendChild(dragPreview);
      e.dataTransfer.setDragImage(dragPreview, rect.width / 2, rect.height / 2);

      // Clean up after drag starts
      setTimeout(() => {
        if (document.body.contains(dragPreview)) {
          document.body.removeChild(dragPreview);
        }
      }, 0);

      // Add wheel event listener for scrolling while dragging
      const handleWheel = (wheelEvent) => {
        if (!scrollContainerRef.current) return;

        const scrollContainer = scrollContainerRef.current;
        wheelEvent.preventDefault();

        // Scroll vertically based on wheel delta
        const scrollSpeed = 5; // Adjust for sensitivity
        const deltaY = wheelEvent.deltaY;

        // Normalize scroll speed (deltaY can vary by browser)
        const scrollAmount = deltaY > 0 ? scrollSpeed : -scrollSpeed;

        scrollContainer.scrollTop += scrollAmount;

        // Also allow horizontal scrolling with Shift + wheel
        if (wheelEvent.shiftKey) {
          const deltaX = wheelEvent.deltaX || wheelEvent.deltaY;
          const scrollAmountX = deltaX > 0 ? scrollSpeed : -scrollSpeed;
          scrollContainer.scrollLeft += scrollAmountX;
        }
      };

      // Use passive: false to allow preventDefault
      document.addEventListener("wheel", handleWheel, { passive: false });
      wheelScrollHandlerRef.current = handleWheel;
    },
    [appointmentBlock, currentAppointment],
  );

  // Handle drag end
  const handleDragEnd = useCallback(() => {
    // Clean up auto-scroll
    if (autoScrollIntervalRef.current) {
      clearInterval(autoScrollIntervalRef.current);
      autoScrollIntervalRef.current = null;
    }

    // Remove wheel event listener
    if (wheelScrollHandlerRef.current) {
      document.removeEventListener("wheel", wheelScrollHandlerRef.current);
      wheelScrollHandlerRef.current = null;
    }

    setDraggedAppointment(null);
    setDragTarget(null);
  }, []);

  // Handle drag over on the container
  const handleDragOver = useCallback(
    (e) => {
      e.preventDefault();
      e.dataTransfer.dropEffect = "move";

      if (!draggedAppointment || !containerRef.current) return;

      const rect = containerRef.current.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;

      // Calculate which day and slot we're over
      const columnWidth = rect.width / 8; // 7 days + 1 time column
      const timeColumnWidth = columnWidth;

      if (x < timeColumnWidth) {
        return;
      }

      const dayIndex = Math.floor((x - timeColumnWidth) / columnWidth);

      if (dayIndex >= 0 && dayIndex < 7) {
        // Account for header row (h-12 = 48px) and calculate slot
        const headerHeight = 48;
        const relativeY = y - headerHeight;
        const slotHeight = 60;
        const slotIndex = Math.max(
          0,
          Math.min(Math.floor(relativeY / slotHeight), timeSlots.length - 1),
        );

        setDragTarget({ dayIndex, slotIndex });
      }
    },
    [draggedAppointment],
  );

  // Handle drag over on individual day columns
  const handleDayDragOver = useCallback((e, dayIndex) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = "move";

    // Always allow drag over - if we're here, something is being dragged
    const dayElement = e.currentTarget;
    const rect = dayElement.getBoundingClientRect();
    const relativeY = e.clientY - rect.top;

    // Calculate which slot we're over (day columns don't include header)
    const slotHeight = 60;
    const slotIndex = Math.max(
      0,
      Math.min(Math.floor(relativeY / slotHeight), timeSlots.length - 1),
    );

    setDragTarget({ dayIndex, slotIndex });

    // Auto-scroll when dragging near edges (horizontal and vertical)
    const scrollThreshold = 50;
    const scrollSpeed = 8;
    const scrollContainer = scrollContainerRef.current;

    if (scrollContainer) {
      const containerRect = scrollContainer.getBoundingClientRect();
      const mouseX = e.clientX;
      const mouseY = e.clientY;

      // Horizontal scrolling: Scroll left (past dates) when near left edge
      if (
        mouseX - containerRect.left < scrollThreshold &&
        scrollContainer.scrollLeft > 0
      ) {
        scrollContainer.scrollLeft -= scrollSpeed;
      }
      // Horizontal scrolling: Scroll right (future dates) when near right edge
      if (containerRect.right - mouseX < scrollThreshold) {
        const maxScroll =
          scrollContainer.scrollWidth - scrollContainer.clientWidth;
        if (scrollContainer.scrollLeft < maxScroll) {
          scrollContainer.scrollLeft += scrollSpeed;
        }
      }

      // Vertical scrolling: Scroll up when near top edge
      if (
        mouseY - containerRect.top < scrollThreshold &&
        scrollContainer.scrollTop > 0
      ) {
        scrollContainer.scrollTop -= scrollSpeed;
      }
      // Vertical scrolling: Scroll down when near bottom edge
      if (containerRect.bottom - mouseY < scrollThreshold) {
        const maxScroll =
          scrollContainer.scrollHeight - scrollContainer.clientHeight;
        if (scrollContainer.scrollTop < maxScroll) {
          scrollContainer.scrollTop += scrollSpeed;
        }
      }
    }
  }, []);

  // Handle drop
  const handleDrop = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();

      // Clean up auto-scroll
      if (autoScrollIntervalRef.current) {
        clearInterval(autoScrollIntervalRef.current);
        autoScrollIntervalRef.current = null;
      }

      // Get dragged appointment from dataTransfer (available in drop event)
      let dragged = draggedAppointment;
      if (!dragged) {
        try {
          const data = e.dataTransfer.getData("application/json");
          if (data) {
            dragged = JSON.parse(data);
            setDraggedAppointment(dragged);
          }
        } catch (err) {
          console.warn("Failed to parse dragged appointment data:", err);
        }
      }

      if (!dragged || !dragTarget) {
        setDraggedAppointment(null);
        setDragTarget(null);
        return;
      }

      const targetDay = days[dragTarget.dayIndex];
      const targetSlot = timeSlots[dragTarget.slotIndex];
      if (!targetSlot) {
        setDraggedAppointment(null);
        setDragTarget(null);
        return;
      }
      const [hours, minutes] = targetSlot.split(":").map(Number);

      const newDateTime = new Date(targetDay.date);
      newDateTime.setHours(hours, minutes, 0, 0);

      // Check if date is in the past (before today)
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const targetDateOnly = new Date(newDateTime);
      targetDateOnly.setHours(0, 0, 0, 0);

      if (targetDateOnly < today) {
        // Don't allow dropping on past dates
        setDraggedAppointment(null);
        setDragTarget(null);
        return;
      }

      // Check if therapist is available at this time slot (matching page.tsx logic)
      if (!isTherapistAvailable(targetSlot, targetDay.date)) {
        // Don't allow dropping on unavailable time slots
        setDraggedAppointment(null);
        setDragTarget(null);
        return;
      }

      // Check if dropping on a break/unavailability block
      const dayData = scheduleData[targetDay.dateStr] || {};
      const allBlocks = [
        ...(dayData.breaks || []),
        ...(dayData.unavailability || []),
      ];

      const isOverBlock = allBlocks.some((block) => {
        const blockStartTime = block.startTime || block.startTimeSlot;
        const blockEndTime = block.endTime || block.endTimeSlot;
        const slotMinutes = timeToMinutes(targetSlot);
        const blockStartMinutes = timeToMinutes(blockStartTime);
        const blockEndMinutes = blockEndTime
          ? timeToMinutes(blockEndTime)
          : blockStartMinutes + (block.duration || 60);

        return (
          slotMinutes >= blockStartMinutes && slotMinutes < blockEndMinutes
        );
      });

      if (isOverBlock) {
        // Don't allow dropping on break/unavailability blocks
        setDraggedAppointment(null);
        setDragTarget(null);
        return;
      }

      if (onAppointmentMove) {
        onAppointmentMove(newDateTime);
      }

      setDraggedAppointment(null);
      setDragTarget(null);
    },
    [
      draggedAppointment,
      dragTarget,
      days,
      onAppointmentMove,
      scheduleData,
      isTherapistAvailable,
    ],
  );

  // Render break/unavailability blocks (matching page.tsx style)
  const renderScheduleBlocks = useCallback(
    (dayIndex, dayData) => {
      const blocks = [];
      const slots = timeSlots; // Use dynamic time slots

      // Render breaks
      const breaks = dayData?.breaks || [];
      breaks.forEach((block, idx) => {
        const startTime = block.startTime || block.startTimeSlot;
        const endTime = block.endTime || block.endTimeSlot;
        const duration =
          block.duration ||
          (startTime && endTime
            ? timeToMinutes(endTime) - timeToMinutes(startTime)
            : 90);

        const dimensions = calculateBlockDimensions(startTime, duration, slots);
        if (!dimensions) return;

        blocks.push(
          <div
            key={`break-${idx}`}
            draggable={false}
            className={cn(
              "absolute inset-x-1 top-0.5 rounded-md border p-1 z-10",
              "bg-gray-100 dark:bg-gray-800 border-gray-200 dark:border-gray-700",
              "cursor-default",
            )}
            style={{
              top: `${dimensions.top}px`,
              height: `${dimensions.height}px`,
            }}>
            <div className="flex justify-between items-start h-full px-1 py-0.5">
              <div className="flex-1 min-w-0 flex flex-col justify-between">
                <div>
                  <div className="flex items-center gap-1">
                    <Badge
                      variant="outline"
                      className="text-xs h-4 px-1 py-0 capitalize bg-gray-100 dark:bg-gray-800">
                      {block.type || "break"}
                    </Badge>
                  </div>
                  <div className="text-[10px] text-gray-600 dark:text-gray-400 mt-0.5">
                    {startTime} - {endTime}
                  </div>
                  {block.note && (
                    <div className="text-[11px] text-gray-700 dark:text-gray-300 italic mt-0.5">
                      {block.note}
                    </div>
                  )}
                </div>
                <div className="flex items-center justify-start mt-1">
                  <Badge
                    variant="secondary"
                    className="text-xs h-4 px-1 py-0 bg-white/80 dark:bg-gray-800/80">
                    {duration}min
                  </Badge>
                </div>
              </div>
            </div>
          </div>,
        );
      });

      // Render unavailability (non-break away times)
      const unavailability = dayData?.unavailability || [];
      unavailability.forEach((block, idx) => {
        const startTime = block.startTime || block.startTimeSlot;
        const endTime = block.endTime || block.endTimeSlot;
        const duration =
          block.duration ||
          (startTime && endTime
            ? timeToMinutes(endTime) - timeToMinutes(startTime)
            : 90);

        const dimensions = calculateBlockDimensions(startTime, duration, slots);
        if (!dimensions) return;

        blocks.push(
          <div
            key={`unavailable-${idx}`}
            draggable={false}
            className={cn(
              "absolute inset-x-1 top-0.5 rounded-md border p-1 z-10",
              "bg-gray-100 dark:bg-gray-800 border-gray-200 dark:border-gray-700",
              "cursor-default",
            )}
            style={{
              top: `${dimensions.top}px`,
              height: `${dimensions.height}px`,
            }}>
            <div className="flex justify-between items-start h-full px-1 py-0.5">
              <div className="flex-1 min-w-0 flex flex-col justify-between">
                <div>
                  <div className="flex items-center gap-1">
                    <Badge
                      variant="outline"
                      className="text-xs h-4 px-1 py-0 capitalize bg-gray-100 dark:bg-gray-800">
                      {block.type || "unavailable"}
                    </Badge>
                  </div>
                  <div className="text-[10px] text-gray-600 dark:text-gray-400 mt-0.5">
                    {startTime} - {endTime}
                  </div>
                  {block.note && (
                    <div className="text-[11px] text-gray-700 dark:text-gray-300 italic mt-0.5">
                      {block.note}
                    </div>
                  )}
                </div>
                <div className="flex items-center justify-start mt-1">
                  <Badge
                    variant="secondary"
                    className="text-xs h-4 px-1 py-0 bg-white/80 dark:bg-gray-800/80">
                    {duration}min
                  </Badge>
                </div>
              </div>
            </div>
          </div>,
        );
      });

      return blocks;
    },
    [timeSlots],
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-12">
        <p className="text-palette-space">Loading schedule...</p>
      </div>
    );
  }

  return (
    <div className="w-full space-y-4">
      {/* Header with date navigation */}
      {onWeekChange && (
        <div className="flex items-center justify-between px-2">
          <button
            onClick={() => {
              const prevWeek = new Date(startDate);
              prevWeek.setDate(prevWeek.getDate() - 7);
              onWeekChange(prevWeek);
            }}
            className="p-2 hover:bg-palette-cloud rounded-lg transition-colors">
            <ArrowLeft className="h-5 w-5 text-palette-phantom" />
          </button>
          <div className="flex items-center gap-2">
            <Calendar className="h-5 w-5 text-palette-phantom" />
            <span className="text-lg font-semibold text-palette-phantom">
              {getWeekHeader(startDate)}
            </span>
          </div>
          <button
            onClick={() => {
              const nextWeek = new Date(startDate);
              nextWeek.setDate(nextWeek.getDate() + 7);
              onWeekChange(nextWeek);
            }}
            className="p-2 hover:bg-palette-cloud rounded-lg transition-colors">
            <ArrowRight className="h-5 w-5 text-palette-phantom" />
          </button>
        </div>
      )}

      {/* Schedule Grid */}
      <div
        ref={containerRef}
        className="border border-palette-smoke/50 rounded-xl overflow-hidden bg-white">
        {/* Scrollable Container */}
        <div
          className="overflow-x-auto overflow-y-auto max-h-[600px]"
          data-scroll-container
          ref={scrollContainerRef}
          style={{ scrollBehavior: "smooth" }}>
          <div className="flex flex-col min-w-fit">
            {/* Header Row - Scrollable with sticky time column */}
            <div className="flex sticky top-0 z-40 bg-white border-b border-palette-smoke/50">
              {/* Time Column Header - Sticky */}
              <div className="sticky left-0 z-50 bg-white p-3 border-r border-palette-smoke/50 flex items-center justify-center min-w-[60px] flex-shrink-0">
                <Clock className="h-4 w-4 text-palette-phantom" />
              </div>
              {/* Day Headers - Scrollable */}
              <div className="flex">
                {days.map((day, idx) => {
                  const today = new Date();
                  today.setHours(0, 0, 0, 0);
                  const dayDateOnly = new Date(day.date);
                  dayDateOnly.setHours(0, 0, 0, 0);
                  const isPastDate = dayDateOnly < today;
                  const isToday = dayDateOnly.getTime() === today.getTime();

                  return (
                    <div
                      key={day.dateStr}
                      className={cn(
                        "p-3 border-r border-palette-smoke/50 min-w-[200px] flex-shrink-0",
                        "text-center",
                        isPastDate && "bg-gray-50/50",
                        isToday && "bg-palette-lime/10",
                      )}>
                      <div className="text-xs text-palette-space mt-1">
                        {day.label}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Time Slots and Schedule Content */}
            <div className="flex">
              {/* Time Column - Sticky/Fixed */}
              <div className="sticky left-0 z-30 bg-white border-r border-palette-smoke/50 min-w-[60px] flex-shrink-0">
                {timeSlots.map((time) => (
                  <div
                    key={time}
                    className="h-[60px] border-b border-palette-smoke/30 flex items-center justify-center text-xs text-palette-space">
                    {time}
                  </div>
                ))}
              </div>

              {/* Day Columns - Scrollable */}
              <div className="flex">
                {days.map((day, dayIndex) => {
                  const dayData = scheduleData[day.dateStr] || {};
                  const hasAppointment =
                    appointmentBlock?.dayIndex === dayIndex;
                  const isDragging =
                    draggedAppointment &&
                    appointmentBlock?.dayIndex === dayIndex;

                  // Check if date is in the past
                  const today = new Date();
                  today.setHours(0, 0, 0, 0);
                  const dayDateOnly = new Date(day.date);
                  dayDateOnly.setHours(0, 0, 0, 0);
                  const isPastDate = dayDateOnly < today;
                  const isToday = dayDateOnly.getTime() === today.getTime();

                  return (
                    <div
                      key={day.dateStr}
                      className={cn(
                        "relative border-r border-palette-smoke/50",
                        "min-h-[480px] w-[200px] flex-shrink-0",
                        dragTarget?.dayIndex === dayIndex &&
                          "bg-palette-lime/5",
                        isPastDate && "bg-gray-50/50",
                      )}
                      onDragOver={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleDayDragOver(e, dayIndex);
                      }}
                      onDrop={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleDrop(e);
                      }}
                      data-day-index={dayIndex}>
                      {/* Grid lines */}
                      {timeSlots.map((time, idx) => {
                        // Check if this time slot is available for the therapist
                        const isAvailable = isTherapistAvailable(
                          time,
                          day.date,
                        );

                        return (
                          <div
                            key={time}
                            className={cn(
                              "h-[60px] border-b border-palette-smoke/30",
                              !isAvailable && "bg-red-50/30 dark:bg-red-900/10",
                            )}
                          />
                        );
                      })}

                      {/* Schedule blocks (breaks, unavailability) - prevent drag over these */}
                      <div
                        className="absolute inset-0 pointer-events-none"
                        style={{ zIndex: 15 }}>
                        {renderScheduleBlocks(dayIndex, dayData)}
                      </div>

                      {/* Current appointment block - matching page.tsx style */}
                      {hasAppointment && (
                        <div
                          draggable
                          onDragStart={handleDragStart}
                          onDragEnd={handleDragEnd}
                          className={cn(
                            "absolute inset-x-1 top-0.5 rounded-md border p-1",
                            "bg-yellow-100 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800",
                            "cursor-move hover:ring-2 hover:ring-blue-400",
                            "group overflow-visible transition-all duration-150",
                            isDragging ? "opacity-60 z-10" : "z-20",
                          )}
                          style={{
                            top: `${appointmentBlock.top}px`,
                            height: `${appointmentBlock.height}px`,
                          }}>
                          <div className="flex justify-between items-start h-full px-1 py-0.5">
                            <div className="flex-1 min-w-0 flex flex-col justify-between">
                              <div>
                                <div className="text-xs font-medium text-gray-900 dark:text-gray-100 truncate">
                                  {currentAppointment.therapist ||
                                    "Appointment"}
                                </div>
                                <div className="text-[10px] text-gray-500 dark:text-gray-500 mt-0.5">
                                  {`${new Date(
                                    currentAppointment.startsAt,
                                  ).toLocaleTimeString("en-US", {
                                    hour: "numeric",
                                    minute: "2-digit",
                                  })} - ${new Date(
                                    new Date(
                                      currentAppointment.startsAt,
                                    ).getTime() +
                                      (currentAppointment.durationMins || 60) *
                                        60000,
                                  ).toLocaleTimeString("en-US", {
                                    hour: "numeric",
                                    minute: "2-digit",
                                  })}`}
                                </div>
                              </div>
                              <div className="flex items-center justify-between mt-1">
                                <Badge
                                  variant="secondary"
                                  className="text-xs h-4 px-1 py-0 bg-white/80 dark:bg-gray-800/80">
                                  {currentAppointment.durationMins || 60}min
                                </Badge>
                                {currentAppointment.status && (
                                  <Badge
                                    variant="outline"
                                    className="text-xs h-4 px-1 py-0 ml-1">
                                    {currentAppointment.status}
                                  </Badge>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Dragged appointment preview */}
                      {dragTarget?.dayIndex === dayIndex && (
                        <div
                          className={cn(
                            "absolute left-1 right-1 rounded-lg border-2 border-dashed",
                            "bg-yellow-400/50 border-yellow-500/50",
                            "flex items-center justify-center",
                            "pointer-events-none z-30",
                            isPastDate && "border-red-300 bg-red-100/50",
                            dragTarget.slotIndex < timeSlots.length &&
                              !isTherapistAvailable(
                                timeSlots[dragTarget.slotIndex],
                                day.date,
                              ) &&
                              "border-red-300 bg-red-100/50",
                          )}
                          style={{
                            top: `${dragTarget.slotIndex * 60}px`,
                            height: `${appointmentBlock?.height || 120}px`,
                          }}>
                          <span
                            className={cn(
                              "text-xs font-medium",
                              isPastDate ||
                                (dragTarget.slotIndex < timeSlots.length &&
                                  !isTherapistAvailable(
                                    timeSlots[dragTarget.slotIndex],
                                    day.date,
                                  ))
                                ? "text-red-700"
                                : "text-gray-700",
                            )}>
                            {isPastDate
                              ? "Cannot drop on past dates"
                              : dragTarget.slotIndex < timeSlots.length &&
                                !isTherapistAvailable(
                                  timeSlots[dragTarget.slotIndex],
                                  day.date,
                                )
                              ? "Therapist not available"
                              : "Drop here to reschedule"}
                          </span>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
